/**
 * V2 Survey Section types. `SurveyQuestionBase` extends V1's `IFormField<string>` (carrying label,
 * fieldType, options, etc. forward) and adds the section/discriminator layer (`_type`, `fieldId`,
 * `visibleWhen`). `_type` mirrors the BE `SurveyPart` JsonPolymorphic discriminator.
 */
import { IFormField } from '@ViewModels';
import { QuestionType } from '../FormFieldWithOptions/questionTypes';

// ---------------------------------------------------------------------------
// Question discriminator
// ---------------------------------------------------------------------------

/** The V2 question discriminator — single source of truth lives in `questionTypes`. */
export type QuestionFieldType = QuestionType;

// ---------------------------------------------------------------------------
// Conditional visibility
// ---------------------------------------------------------------------------

export interface QuestionVisibilitySimple {
	_type: 'SimpleEqualityQuestionVisibility';
	fieldId: string;
	fieldValue: string;
}

export interface QuestionVisibilityLogical {
	_type: 'LogicalQuestionVisibility';
	visibilities: QuestionVisibility[];
	logicalOperator: 'And' | 'Or';
}

export type QuestionVisibility = QuestionVisibilitySimple | QuestionVisibilityLogical;

// ---------------------------------------------------------------------------
// Survey questions — extend V1 IFormField<string> to carry forward all
// existing question fields (label, fieldType, options, isOptional, etc.)
// ---------------------------------------------------------------------------

// Omit `_type` from IFormField (its ApiModelType union excludes V2 discriminators) and re-add ours.
interface SurveyQuestionBase extends Omit<IFormField<string>, '_type'> {
	id: string;
	fieldId: string;
	_type: QuestionFieldType;
	visibleWhen?: QuestionVisibility;
	// Scale config (LinearScale/NPS only) — lives on the surveyPart, round-trips via surveyForm.
	minValue?: number;
	maxValue?: number;
	startLabel?: string | null;
	endLabel?: string | null;
}

export interface ShortAnswerQuestion extends SurveyQuestionBase {
	_type: 'ShortAnswerQuestion';
}

export interface LongAnswerQuestion extends SurveyQuestionBase {
	_type: 'LongAnswerQuestion';
}

export interface DropDownQuestion extends SurveyQuestionBase {
	_type: 'DropDownQuestion';
}

export interface CheckBoxesQuestion extends SurveyQuestionBase {
	_type: 'CheckBoxesQuestion';
}

export interface FileUploadQuestion extends SurveyQuestionBase {
	_type: 'FileUploadQuestion';
}

export interface LinearScaleQuestion extends SurveyQuestionBase {
	_type: 'LinearScaleQuestion';
}

export interface NpsQuestion extends SurveyQuestionBase {
	_type: 'NpsQuestion';
}

export type SurveyQuestion =
	| ShortAnswerQuestion
	| LongAnswerQuestion
	| DropDownQuestion
	| CheckBoxesQuestion
	| FileUploadQuestion
	| LinearScaleQuestion
	| NpsQuestion;

// ---------------------------------------------------------------------------
// Survey section
// ---------------------------------------------------------------------------

export interface SurveySection {
	_type: 'SurveySection';
	id: string;
	title?: string;
	description?: string;
	questions: SurveyQuestion[];
	visibleWhen?: QuestionVisibility;
}

// ---------------------------------------------------------------------------
// Top-level union and type guards
// ---------------------------------------------------------------------------

export type SurveyPart = SurveySection | SurveyQuestion;

export const isSurveySection = (part: SurveyPart): part is SurveySection => part._type === 'SurveySection';
